import React, { useState } from 'react'
import { useFormik } from 'formik'
import {
  Flex,
  message as messagePopup,
  message as messageAntd,
  Table,
} from 'antd'
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh'
import VerifiedIcon from '@mui/icons-material/Verified'

import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import PageUrls from '../../../../constants/page-urls'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import { OrderUtil } from '../../../../utils/order.util'
import { PropertyList } from '../../../../components/PropertyList'
import { PropertyListItem } from '../../../../components/PropertyListItem'
import { IProductTable } from '../../../../types/product'
import { axiosInstance } from '../../../../hooks/useAxios'
import { validateMinMaxValue } from '../../../../utils/validateMinMaxValue'
import { useDispatch } from 'react-redux'
import {
  closeModal,
  openModal,
} from '../../../../../store/reducers/modal/modal.slice'
import ConfirmOrderModal from '../../../../components/ConfirmOrderModal'
import { ERRORS_MESSAGE_MAPPER } from '../../../../constants/errors'
import { useRecipientPhoneRule } from '../../../../hooks/useRecipientPhoneRule'
import { useUser } from '../../../../hooks/useUser'
import { resolveRecipientPhone } from '../../../../utils/recipient-phone-rule.util'

const Menus: any[] = []

const OrderQuickCreate = () => {
  const dispatch = useDispatch()
  const { merchant } = useUser()
  const recipientPhoneRule = useRecipientPhoneRule()
  const [message, setMessage] = useState<string>('')
  const [isExtractMessage, setIsExtractMessage] = useState<boolean>(false)

  const handleCheckCustomerWarning = async (values: any) => {
    try {
      const responseDuplicateOrder = await axiosInstance.post<{
        isDuplicate: boolean
      }>('/order/check/duplicate', {
        name: values.name,
        phone: values.phone,
        day: merchant.config?.dulplicateOrderDate ?? 1,
      })
      let isWarning
      const title = []
      if (responseDuplicateOrder?.data?.isDuplicate) {
        isWarning = true
        title.push(
          `เตือน! ลูกค้า ${values.name} มีคำสั่งซื้อซ้ำในระยะเวลา ${
            merchant.config?.dulplicateOrderDate ?? 1
          } วันที่ผ่านมา `,
        )
      }

      const responseCustomerWarning = await axiosInstance.post(
        '/blacklists/checking',
        {
          name: values.name,
          phoneNumber: values.phone,
          ignoreName: true,
        },
      )
      if (responseCustomerWarning.data.blacklist) {
        isWarning = true
        title.push(`ลูกค้า ${values.name} อยู่ในรายการ Blacklist`)
      }

      if (isWarning) {
        dispatch(
          openModal({
            title: title.join('และ '),
            width: 500,
            content: (
              <ConfirmOrderModal
                onConfirm={async () => {
                  formik.setValues({
                    name: values.name,
                    phone: values.phone,
                    address: values.other,
                    subDistrict: values.tambon,
                    district: values.amphoe,
                    province: values.province,
                    zipcode: values.postcode,
                    isCOD: values.isCod,
                    note: values.note,
                    commissionUsername: values.commissionUser,
                    social: values.social,
                    channel: values.channel,
                    products: values.products,
                    orderDate: '',
                  })
                  dispatch(closeModal())
                }}
              />
            ),
          }),
        )
      } else {
        formik.setValues({
          name: values.name,
          phone: values.phone,
          address: values.other,
          subDistrict: values.tambon,
          district: values.amphoe,
          province: values.province,
          zipcode: values.postcode,
          isCOD: values.isCod,
          note: values.note,
          commissionUsername: values.commissionUser,
          social: values.social,
          channel: values.channel,
          products: values.products,
          orderDate: '',
        })
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      messageAntd.error(messageError)
    }
  }

  const formik = useFormik<any>({
    initialValues: {
      name: '',
      phone: '',
      address: '',
      subDistrict: '',
      district: '',
      province: '',
      commissionUsername: '',
      zipcode: '',
      social: '',
      channel: {
        objectId: '',
        name: '',
      },
      isCOD: false,
      products: [],
      orderDate: '',
      note: '',
    },
    onSubmit: async (values) => {
      const { check: phoneCheck, phone } = resolveRecipientPhone(
        values.phone,
        recipientPhoneRule,
      )
      if (phoneCheck.status === 'invalid') {
        messageAntd.error(phoneCheck.message)
        return
      }
      try {
        await axiosInstance.post('/order', {
          name: values.name,
          phone,
          address: {
            address: values.address.trim(),
            subDistrict: values.subDistrict.trim(),
            district: values.district.trim(),
            province: values.province.trim(),
            zipcode: values.zipcode.trim(),
          },
          commissionUsername: values.commissionUsername,
          facebook: values.social,
          distribution: values.channel.objectId,
          isCOD: values.isCOD,
          orderDate: new Date(),
          products: values.products.map((product: any) => ({
            objectId: product?.objectId,
            quantity: product.amount,
            discount: product.discount,
          })),
        })
        formik.resetForm()
        // setMessage('')
        messagePopup.success('บันทึกข้อมูลสำเร็จ')
      } catch (e: any) {
        const messageError =
          ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
          'บันทึกข้อมูลไม่สำเร็จ'
        messageAntd.error(messageError)
      }
    },
  })

  const onChangeDiscount = (index: number, value: any) => {
    const prevCollections: any[] = formik.values.products
    prevCollections[index].discount = value
    prevCollections[index].totalPrice =
      prevCollections[index].price * prevCollections[index].amount - value
    prevCollections[index].totalCost =
      prevCollections[index].cost * prevCollections[index].amount - value
    formik.setFieldValue('products', prevCollections)
  }

  const getTotal = () => {
    return formik.values.products.reduce(
      (total: number, product: any) =>
        total + ((product.totalPrice || 0) < 0 ? 0 : product.totalPrice),
      0,
    )
  }

  const handleSubmit = async () => {
    formik.handleSubmit()
  }

  const handleExtractOrderMessage = async () => {
    try {
      setIsExtractMessage(true)
      const data = await OrderUtil.extractOrderMessage(message)
      const {
        address: {
          name,
          phone,
          address,
          subDistrict,
          district,
          province,
          zipcode,
        },
        isCod,
        commissionUser,
        social,
        channel,
        note,
        products,
      } = data

      const info = {
        name: name,
        phone: phone,
        other: address,
        tambon: subDistrict,
        amphoe: district,
        province: province,
        postcode: zipcode,
        isCod: isCod,
        commissionUser: commissionUser,
        social: social,
        channel: channel,
        note: note,
        products: products,
      }
      console.log(info)
      handleCheckCustomerWarning(info)
    } catch (e) {
      messagePopup.error('ไม่สามารถนำเข้าข้อมูลได้')
    }
  }

  const columns = [
    {
      title: 'รายการสินค้า',
      dataIndex: 'product',
      key: 'name',
      width: 220,
      render: (product: IProductTable) => {
        return (
          <Flex vertical>
            <Typography variant="subtitle1" color="text.secondary">
              {product?.name}
            </Typography>
            <Typography variant="subtitle2" color="text.secondary">
              {product?.code}
            </Typography>
          </Flex>
        )
      },
    },
    {
      title: 'ราคาต่อชิ้น',
      dataIndex: 'product',
      key: 'price',
      align: 'right' as 'right',
      width: 100,
      render: (product: IProductTable) => (
        <Typography variant="subtitle2" color="text.secondary">
          {product?.price ?? 0}
        </Typography>
      ),
    },
    {
      title: 'จำนวน',
      dataIndex: 'product',
      key: 'quantity',
      align: 'right' as 'right',
      width: 80,
      render: (product: IProductTable) => (
        <Typography variant="subtitle2" color="text.secondary">
          {product?.amount}
        </Typography>
      ),
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'key',
      key: 'discount',
      width: 100,
      render: (key: number) => (
        <TextField
          fullWidth
          label="ส่วนลด"
          name="discount"
          value={formik.values.products[key!].discount || ''}
          type={'number'}
          onKeyPress={(e) => {
            if (['+', '-', 'e', '*', '.'].includes(e.key)) {
              e.preventDefault()
            }
          }}
          onChange={(e) => {
            onChangeDiscount(
              key!,
              validateMinMaxValue(
                e.target.value,
                0,
                formik.values.products[key!].price *
                  formik.values.products[key!].amount || 1,
              ) || '',
            )
          }}
          helperText={
            formik.touched.products && (formik.errors.products as string)
          }
          error={!!(formik.touched.products && formik.errors.products)}
        />
      ),
    },
    {
      title: 'ราคารวม',
      dataIndex: 'product',
      key: 'total',
      align: 'right' as 'right',
      width: 100,
      render: (product: IProductTable) => (
        <Typography variant="subtitle2" color="text.secondary">
          {product?.totalPrice ?? 0}
        </Typography>
      ),
    },
  ]

  return (
    <ContentWrapper>
      <Helmet title="คำสั่งซื้อใหม่ (ด่วน)" />
      <HeaderContent
        goBack
        title="คำสั่งซื้อใหม่ (ด่วน)"
        routes={Menus}
        descriptions="รายการคำสั่งซื้อสินค้า"
      ></HeaderContent>

      <Box
        sx={{ flexGrow: 1 }}
        component="form"
        mt={1}
        onSubmit={formik.handleSubmit}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} lg={5}>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={12}>
                <Card sx={{ padding: 2 }}>
                  <Typography variant={'h6'} mb={2}>
                    รายการคำสั่งซื้อใหม่
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={12}>
                      <TextField
                        fullWidth
                        autoFocus={true}
                        label={`ตัวช่วยจำแนกคำสั่งซื้อ`}
                        multiline
                        placeholder={`คุณมานี มานะ 0831725655 
555/26 หมู่บ้านเมโทรทวน์ ต.เขตพระนคร อ.แขวงเสาชิงช้า จ.กรุงเทพมหานคร 10200
                    
T1001/2/-300
H0012/1
A5076/4/-200
code/1/-200
                    
c: facebook-adsssss
s: asfdgdfg

k: คนได้รับค่าคอมมิชชั่น
                    
COD/เก็บปลายทาง/เงินสด/ชำระแล้ว
                      `}
                        rows={18}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12} lg={12}>
                      <Button
                        startIcon={<AutoFixHighIcon />}
                        onClick={() => handleExtractOrderMessage()}
                        fullWidth
                        variant={'contained'}
                      >
                        นำเข้าคำสั่งซื้อ
                      </Button>
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
              <Grid item xs={12} lg={12}>
                <Card sx={{ padding: 2 }}>
                  <Typography variant={'h6'} mb={2}>
                    ตัวอย่าง
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={12}>
                      <TextField
                        fullWidth
                        disabled
                        autoFocus={true}
                        multiline
                        value={`ตัวอย่าง ตัวช่วยจำแนกคำสั่งซื้อ
คุณมานี มานะ 0831725655 
555/26 หมู่บ้านเมโทรทวน์ ต.เขตพระนคร อ.แขวงเสาชิงช้า จ.กรุงเทพมหานคร 10200
                    
T1001/2/-300
H0012/1
A5076/4/-200
code/1/-200
                    
c: facebook-adsssss
s: asfdgdfg

k: คนได้รับค่าคอมมิชชั่น
                    
COD/เก็บปลายทาง/เงินสด/ชำระแล้ว
                      `}
                        rows={18}
                      />
                    </Grid>
                  </Grid>
                </Card>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} lg={7}>
            <Card sx={{ mb: 2 }}>
              <CardHeader title="รายละเอียดใบสั่งซื้อ" />
              <Divider />
              <PropertyList>
                <PropertyListItem
                  align={`horizontal`}
                  label="ชื่อ-นามสกุล"
                  icon={formik.values.name && <VerifiedIcon />}
                >
                  <Typography variant="subtitle1">
                    {formik.values.name || `รอข้อมูล...`}
                  </Typography>

                  {isExtractMessage ? (
                    <>
                      <Typography color="text.secondary" variant="body2">
                        {formik.values.address}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {`${formik.values.subDistrict} ${formik.values.district}`}
                      </Typography>
                      <Typography color="text.secondary" variant="body2">
                        {`${formik.values.province} ${formik.values.zipcode}`}
                      </Typography>
                    </>
                  ) : (
                    <Typography color="text.secondary" variant="body2">
                      รอข้อมูล...
                    </Typography>
                  )}
                </PropertyListItem>
                <Divider />
                <PropertyListItem
                  align={`horizontal`}
                  label="เบอร์โทรศัพท์"
                  value={
                    isExtractMessage
                      ? formik.values?.phone || `ไม่พบรายละเอียด...`
                      : 'รอข้อมูล...'
                  }
                  icon={formik.values?.phone && <VerifiedIcon />}
                />
                <Divider />
                <PropertyListItem
                  align={`horizontal`}
                  label="รูปแบบการชำระเงิน"
                  value={
                    isExtractMessage
                      ? formik.values?.isCOD
                        ? 'เก็บปลายทาง'
                        : 'ชำระแล้ว'
                      : 'รอข้อมูล...'
                  }
                  icon={formik.values?.isCOD && <VerifiedIcon />}
                />
                <Divider />
                <PropertyListItem
                  align={`horizontal`}
                  label="ช่องทางโซเชียล"
                  value={
                    isExtractMessage
                      ? formik.values?.social || `ไม่พบรายละเอียด...`
                      : 'รอข้อมูล...'
                  }
                  icon={formik.values?.social && <VerifiedIcon />}
                />
                <Divider />
                <PropertyListItem
                  align={`horizontal`}
                  label="ช่องทางการสั่งซื้อ"
                  value={
                    isExtractMessage
                      ? formik.values?.channel?.name || `ไม่พบรายละเอียด...`
                      : 'รอข้อมูล...'
                  }
                  icon={formik.values?.channel?.name && <VerifiedIcon />}
                />
                <Divider />
                <PropertyListItem
                  align={`horizontal`}
                  label="ชื่อผู้รับค่าคอมมิชชั่น"
                  value={
                    isExtractMessage
                      ? formik.values?.commissionUsername ||
                        `ไม่พบรายละเอียด...`
                      : 'รอข้อมูล...'
                  }
                  icon={formik.values?.commissionUsername && <VerifiedIcon />}
                />
                <Divider />
                <PropertyListItem
                  align={`horizontal`}
                  label="หมายเหตุ"
                  value={
                    isExtractMessage
                      ? formik.values?.note || `ไม่พบรายละเอียด...`
                      : 'รอข้อมูล...'
                  }
                  icon={formik.values?.note && <VerifiedIcon />}
                />
              </PropertyList>
            </Card>
            <Card>
              <CardHeader title="รายการสินค้า" />
              <Divider />
              <CardContent>
                <Table
                  size={'small'}
                  columns={columns}
                  dataSource={formik.values?.products?.map(
                    (product: IProductTable, index: number) => {
                      return {
                        product,
                        key: index,
                      }
                    },
                  )}
                  bordered
                  pagination={{ hideOnSinglePage: true }}
                />
                <Grid container justifyContent={'flex-end'} mt={2} mb={2}>
                  <Grid item>
                    <Typography variant="subtitle2" color="text.secondary">
                      ราคารวม: {getTotal().toLocaleString()} บาท
                    </Typography>
                  </Grid>
                </Grid>
                <form onSubmit={formik.handleSubmit}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} lg={12}>
                      <Button
                        startIcon={<SaveIcon />}
                        onClick={() => handleSubmit()}
                        fullWidth
                        variant={'contained'}
                      >
                        บันทึกคำสั่งซื้อ
                      </Button>
                    </Grid>
                  </Grid>
                </form>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </ContentWrapper>
  )
}

export default OrderQuickCreate
